import { useEffect, useState } from 'react';

export default function useMedia<T = string>(queries: string[], values: T[], defaultValue: T): T {
  // Safe default state: assume defaultValue for SSR
  const [value, setValue] = useState<T>(defaultValue);

  useEffect(() => {
    // Ensure this only runs on the client
    if (typeof window === 'undefined') return;

    const mediaQueryLists = queries.map((q) => window.matchMedia(q));

    const getValue = () => {
      const index = mediaQueryLists.findIndex((mql) => mql.matches);
      return typeof values[index] !== 'undefined' ? values[index] : defaultValue;
    };

    // Set the initial value after hydration
    setValue(getValue());

    const handler = () => setValue(getValue());

    mediaQueryLists.forEach((mql) => {
      if (mql.addEventListener) {
        mql.addEventListener('change', handler);
      } else {
        // Support for older Safari
        mql.addListener(handler);
      }
    });

    return () => {
      mediaQueryLists.forEach((mql) => {
        if (mql.removeEventListener) {
          mql.removeEventListener('change', handler);
        } else {
          mql.removeListener(handler);
        }
      });
    };
  }, [queries, values, defaultValue]);

  return value;
}
